import { useMemo } from 'react';
import styled from 'styled-components';

import { AccessModes, Metrics, Sources } from 'components/insights/constants';
import { EM_DASH, formatWholeNumber } from 'components/insights/formatting';
import MetricTile from 'components/insights/layout/tiles/MetricTile';
import { ReviewType } from 'models/Review';
import { useNetworkPerformanceActions } from 'stores/networkPerformanceStore';
import ResponseTimeIcon from 'svg/ResponseTimeIcon';
import SafeReturnIcon from 'svg/SafeReturnIcon';

import useNetworkPerformanceAnalytics from '../query/useNetworkPerformanceAnalytics';
import { METRIC_CONFIGS, NetworkPerformanceMetrics } from '../slideout/constants';

// Figma-specific dark green for safe return icons — no matching theme token
const SAFE_RETURN_ICON_COLOR = '#0A6B15';

export default function SafeReturnOverviewTile() {
  const { openSlideout } = useNetworkPerformanceActions();
  const queryParams = useMemo(
    () => ({
      source: Sources.REVIEWS,
      accessModes: [AccessModes.MANAGER],
      reviewType: ReviewType.SAFE_RETURN,
      metrics: [Metrics.SAFE_RETURNS_REQUESTED_COUNT, Metrics.SAFE_RETURN_RESPONSE_TIME_AVERAGE],
    }),
    []
  );

  const { loading, data } = useNetworkPerformanceAnalytics(queryParams, {
    queryKey: 'networkPerformanceSafeReturns',
  });

  const safeReturnsRequested = data.metrics.getCurrentSeries(Metrics.SAFE_RETURNS_REQUESTED_COUNT)[0];
  const avgResponseTime = data.metrics.getCurrentSeries(Metrics.SAFE_RETURN_RESPONSE_TIME_AVERAGE)[0];

  const hasData = safeReturnsRequested != null;

  const formattedResponseTime = useMemo(() => {
    if (avgResponseTime == null || isNaN(avgResponseTime)) return EM_DASH;
    const totalMinutes = Math.round(avgResponseTime / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0 && minutes === 0) return '<1m';
    return `${hours}h ${minutes}m`;
  }, [avgResponseTime]);

  return (
    <MetricTile
      label={METRIC_CONFIGS[NetworkPerformanceMetrics.SAFE_RETURNS].label}
      showValue={false}
      loading={loading}
      hasData={hasData}
      onClick={() => openSlideout(NetworkPerformanceMetrics.SAFE_RETURNS)}>
      <SubCardsWrapper>
        <SubCard>
          <SubCardContent>
            <SubCardLabel>Safe Returns Requested</SubCardLabel>
            <SubCardValue>{formatWholeNumber(safeReturnsRequested)}</SubCardValue>
          </SubCardContent>
          <SubCardIcon>
            <SafeReturnIcon width={24} height={24} style={{ color: SAFE_RETURN_ICON_COLOR }} />
          </SubCardIcon>
        </SubCard>
        <SubCard>
          <SubCardContent>
            <SubCardLabel>Avg. Safe Return Response Time</SubCardLabel>
            <SubCardValue>{formattedResponseTime}</SubCardValue>
          </SubCardContent>
          <SubCardIcon>
            <ResponseTimeIcon width={22} height={22} style={{ color: SAFE_RETURN_ICON_COLOR, marginRight: 3 }} />
          </SubCardIcon>
        </SubCard>
      </SubCardsWrapper>
    </MetricTile>
  );
}

const SubCardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const SubCard = styled.div`
  background-color: rgba(17, 175, 34, 0.05);
  border-radius: 4px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
`;

const SubCardContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubCardLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};
`;

const SubCardValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black100};
  margin-top: 8px;
`;

const SubCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 16px;
`;
